import { useRef, useCallback } from 'react';
import { logger } from '@/core/services/logger';
import { supabase } from '@/core/services/supabase';

export interface Message {
  role: 'user' | 'assistant';
  content: string;
  isError?: boolean;
}

export type ConnectionState = 'idle' | 'connecting' | 'streaming' | 'error';

async function streamChatWithRetry(
  messages: Message[],
  onChunk: (content: string) => void,
  onComplete: () => void,
  onError: (error: Error) => void,
  abortSignal?: AbortSignal
): Promise<void> {
  const MAX_RETRIES = 3;
  const BASE_DELAY = 1000;
  const TIMEOUT_MS = 60000;

  const { data: { session }, error: sessionError } = await supabase.auth.getSession();

  if (sessionError || !session?.access_token) {
    onError(new Error('Please sign in to use the chat assistant.'));
    return;
  }

  let lastError: Error | null = null;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

      if (abortSignal?.aborted) {
        throw new Error('Request cancelled');
      }
      abortSignal?.addEventListener('abort', () => controller.abort());

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/dr-phillip-chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            messages: messages.filter(m => !m.isError).map(m => ({ role: m.role, content: m.content }))
          }),
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');

        if (response.status === 429) {
          throw new Error('I\'m helping too many people right now. Please try again in a moment.');
        }
        if (response.status === 402) {
          throw new Error('Service temporarily unavailable. Please contact support.');
        }
        if (response.status === 401) {
          throw new Error('Session expired. Please refresh the page.');
        }
        if (response.status >= 500) {
          throw new Error(`Server error: ${response.status}`);
        }
        throw new Error(errorText || 'Failed to get response');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              if (data === '[DONE]') continue;

              try {
                const parsed = JSON.parse(data);
                const content = parsed.choices?.[0]?.delta?.content ||
                               parsed.delta?.text ||
                               parsed.content?.[0]?.text;
                if (content) {
                  assistantMessage += content;
                  onChunk(assistantMessage);
                }
              } catch {
                // Skip invalid JSON chunks
              }
            }
          }
        }
      }

      onComplete();
      return;

    } catch (error: unknown) {
      lastError = error;

      if (error.name === 'AbortError' || error.message === 'Request cancelled') {
        onError(new Error('Request cancelled'));
        return;
      }

      if (error.message?.includes('Session expired') ||
          error.message?.includes('sign in') ||
          error.message?.includes('too many people')) {
        onError(error);
        return;
      }

      if (attempt < MAX_RETRIES - 1) {
        const delay = BASE_DELAY * Math.pow(2, attempt);
        logger.info(`[DrPhillip] Retry ${attempt + 1}/${MAX_RETRIES} after ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  onError(lastError || new Error('Failed to connect after multiple attempts. Please try again.'));
}

export function useStreamingChat() {
  const abortControllerRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(async (
    messages: Message[],
    onChunk: (content: string) => void,
    onComplete: () => void,
    onError: (error: Error) => void
  ): Promise<void> => {
    abortControllerRef.current = new AbortController();
    await streamChatWithRetry(messages, onChunk, onComplete, onError, abortControllerRef.current.signal);
  }, []);

  const cancelRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  return { sendMessage, cancelRequest };
}
